import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { bindActionCreators } from "redux";
import { useDispatch, useSelector } from "react-redux";
import * as bulkJobActions from "../../../modules/bulkJobs";
import { listJobs } from "../../../modules/bulkJobs";
import * as notificationActions from "../../../modules/notification";
import { setNotification } from "../../../modules/notification";
import { JOB_ACTIONS, UPLOAD_LOCATION } from "@constants";
import CreateAssetsBulkJobMessage from "../../../components/modals/components/CreateAssetsBulkToast";
import menuService from "@services/menuService";
import { Alert, Box, Divider, Grid, PDCAlpha as alpha, Stack, Tooltip, Typography } from "@mzc-pdc/ui";
import { IconCompleteCircle, IconInfoCircle, IconWarningTriangle } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import * as jobStatus from "@cores/jobStatus";
import { SelectLimitTags } from "@components_v2/select";
import JobEmptyList from "@routes/rightsidebar/ui/job-empty-list";
import { copyAsset } from "@modules/asset";
import JobItem from "@routes/rightsidebar/ui/job-item";
import useMyJobs from "@hooks/useMyJobs";
import { updateMyJobsJob } from "@modules/myJobs";
import useVirtualFolderPath from "@hooks/useVirtualFolderPath";
import { capitalize } from "@utils/convert-string";

let throttleHandler = false;

const CreateAssetsPanel = ({ filter, jobIcon, summaries, taskComponent }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const { getVirtualFolderFullPath, getDriveName } = useVirtualFolderPath({ domain: "ASSET" });
    const { synchronize } = useMyJobs({ resource: "createAsset", store: "uploadQueue" });

    const [limit, setLimit] = useState(20);
    const [viewmoreEnable, setViewmoreEnable] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState({});

    const { bulkJobs } = useSelector((state) => state);
    const createAssetJobs = useSelector(({ myJobs }) => myJobs.createAsset);
    const createAssetUploads = useSelector(({ uploadQueue }) => uploadQueue?.[UPLOAD_LOCATION.CREATE_ASSET] ?? []);
    const panelActive = useSelector(({ rightSideBar }) => rightSideBar.active);

    //Note: 자산 생성 업로드는 브라우저가 전송하므로 페이지를 닫거나 새로고침하면 그 자리에서 끊긴다.
    //집계 단위는 잡이다(이탈로 잃는 단위가 잡이라서). 판정식은 hooks/useMyJobs.js 의 isJobProcessing 과 같다.
    //배치 근거는 docs/design-handoff/DAM-607-design/README.md 참고.
    const uploadingCount = createAssetUploads.filter((item) => !jobStatus.isComplete(item.status)).length;

    //Note: 해제 문구(「이제 닫아도 됩니다」)는 패널을 닫을 때까지 남긴다 — 수십 분 걸리는 작업이라
    //몇 초 뒤 사라지면 돌아온 사람이 못 본다. 패널을 닫아도 언마운트되지 않으므로(RightSideBar 는 CSS
    //클래스만 토글하고 TabPanel 은 hidden 으로 감춘다) 마운트 수명 대신 active 를 초기화 신호로 쓴다.
    //사이드바가 다른 용도로 열리면(type 변경) 언마운트돼 latch 가 풀린다 — 해제 문구를 놓칠 뿐 위험하지 않다.
    const infoIconRef = useRef(null);
    const sawUploadingRef = useRef(false);
    if (!panelActive) sawUploadingRef.current = false;
    else if (uploadingCount > 0) sawUploadingRef.current = true;

    const uploadGuide = useMemo(() => {
        if (uploadingCount > 0) {
            return {
                severity: "warning",
                accent: theme.palette.warning[700],
                icon: <IconWarningTriangle size={16} htmlColor={theme.palette.warning[700]} />,
                title: t(`common::msg::${"{{count}} upload(s) transferring — do not close or reload this page"}`, {
                    count: uploadingCount,
                }),
                description: t(
                    `common::msg::${"Moving between menus or closing this panel is fine."}`,
                    `Moving between menus or closing this panel is fine.`,
                ),
            };
        }

        if (sawUploadingRef.current && createAssetUploads.length > 0) {
            return {
                severity: "success",
                accent: theme.palette.success[700],
                icon: <IconCompleteCircle size={16} htmlColor={theme.palette.success[700]} />,
                title: t(`common::msg::${"{{count}} upload(s) transferred — you can close this page"}`, {
                    count: createAssetUploads.length,
                }),
                description: t(
                    `common::msg::${"Processing continues on the server."}`,
                    `Processing continues on the server.`,
                ),
            };
        }

        return null;
    }, [uploadingCount, createAssetUploads.length, panelActive, t]);

    const dispatch = useDispatch();

    const BulkJobActions = bindActionCreators(bulkJobActions, dispatch);
    const NotificationActions = bindActionCreators(notificationActions, dispatch);

    useEffect(() => {
        synchronize();
    }, []);

    useEffect(() => {
        if (bulkJobs.totalCounts > bulkJobs.data.length) {
            setViewmoreEnable(true);
        } else {
            setViewmoreEnable(false);
        }
    }, [bulkJobs.data]);

    useEffect(() => {
        BulkJobActions.updateSearch({ limit });
    }, [limit]);

    const onChangeLimit = useCallback(async (limit) => {
        setLimit(limit);
        await BulkJobActions.listJobs();
    }, []);

    const onSubmit = useCallback(async () => {
        await BulkJobActions.updateSearch({ limit: bulkJobs.search.limit + limit });
        await BulkJobActions.listJobs();
    }, []); //archive, restore(목록페이지에서 job 요청) / -> job 목록 요청 -> job 완료시 task에 일부 실패가 있으면 토스트(panel에서 담당)

    const toggleJobIsExpanded = useCallback(
        async (action, item) => {
            if (throttleHandler) {
                return;
            }
            if (!item.isFolded) {
                const job = {
                    ...item,
                    isFolded: !item.isFolded,
                };

                if (action !== "UPLOAD_ASSET") await BulkJobActions.updateJob({ job });
                else dispatch(updateMyJobsJob({ resource: "createAsset", jobId: item.jobId, data: job }));
            } else {
                if (action !== "UPLOAD_ASSET") {
                    throttleHandler = true;
                    try {
                        const { data } = await BulkJobActions.listTasks({ jobId: item.jobId });
                        const newTasks = data.results || [];
                        await Promise.all(
                            newTasks.map(async (task) => {
                                if (task.metadata.virtualFolder?.id) {
                                    task.folderFullPath = await getVirtualFolderFullPath({
                                        folderId: task.metadata.virtualFolder?.id,
                                    });
                                } else if (task.metadata.virtualFolder?.fullpath !== undefined) {
                                    const driveName = await getDriveName({
                                        driveId: task.metadata.virtualFolder.driveId,
                                    });
                                    task.folderFullPath = `${driveName}/${task.metadata.virtualFolder.fullpath}`;
                                }
                            }),
                        );
                        const job = {
                            ...item,
                            isFolded: item.isFolded === undefined ? false : !item.isFolded,
                            tasks: newTasks,
                        };
                        await BulkJobActions.updateJob({ job });
                        throttleHandler = false;
                    } catch (error) {
                        throttleHandler = false;
                    }
                } else {
                    const job = {
                        ...item,
                        isFolded: !item.isFolded,
                    };

                    dispatch(updateMyJobsJob({ resource: "createAsset", jobId: item.jobId, data: job }));
                }
            }
        },
        [getVirtualFolderFullPath, getDriveName],
    );

    const toastInfoMessage = useCallback((checkedAssets) => {
        NotificationActions.setNotification({
            type: "info",
            autoClose: false,
            contents: (
                <CreateAssetsBulkJobMessage
                    firstAssetMediaType={checkedAssets[0].mediaType}
                    count={checkedAssets.length}
                />
            ),
        });
    }, []);

    const onRetry = useCallback(async (job) => {
        if (job.action === JOB_ACTIONS.CREATE_ASSETS) {
            const { data } = await BulkJobActions.listTasks({ jobId: job.jobId });
            const tasks = data.results || [];
            const failedTasks = tasks.filter((t) => t.status === "FAILED");
            if (failedTasks.length > 0) {
                const assets = failedTasks.map((t) => {
                    return t.metadata;
                });
                const newJob = {
                    action: JOB_ACTIONS.CREATE_ASSETS,
                    params: {
                        items: assets,
                    },
                };
                await BulkJobActions.createJob(newJob);
                toastInfoMessage(assets);
                await BulkJobActions.listJobs();
            }
        } else if (job.action === JOB_ACTIONS.COPY_ASSETS) {
            const metadata = job.metadata;
            const params = {
                destination: {
                    ...metadata.destination,
                },
                source: {
                    assets: [...metadata.source.assets],
                },
            };

            await dispatch(copyAsset(params));
            await dispatch(listJobs());
            await dispatch(
                setNotification({
                    type: "success",
                    contents: t(`common::msg::${"Asset {{item}} duplication completed."}`, {
                        item: metadata.source.assets?.[0].name,
                    }),
                }),
            );
        }
    }, []);

    const onHandleClickFilter = useCallback(
        (id, value) => {
            setSelectedFilter({
                ...selectedFilter,
                [id]: value,
            });
        },
        [selectedFilter],
    );

    const getJobStatus = useCallback(
        (item) => {
            if (!item || !createAssetJobs.data) return "";
            const status = item.status;

            if (status === "FAILED" || item.summary?.detail?.failCount === item.taskCount) return "ERROR";
            else return status;
        },
        [createAssetJobs.data],
    );

    const jobsData = bulkJobs.data;

    const filteredJobsData = useMemo(() => {
        const createAssetJobData = createAssetJobs.data ?? [];

        const filteredJobsData = [...createAssetJobData, ...jobsData]
            .sort((prev, post) => new Date(post) - new Date(prev))
            .filter((job) =>
                Object.keys(selectedFilter)
                    .filter((key) => selectedFilter[key]?.length)
                    .every((key) => selectedFilter[key].includes(job[key])),
            );

        const addValueToSummaries = (job) => {
            return summaries.map(({ key, ...rest }) => {
                return {
                    ...rest,
                    value: job[key],
                };
            });
        };

        return filteredJobsData.map((job) => ({
            job: {
                id: job.jobId,
                action: job.action,
                icon: jobIcon[job.action],
                status: getJobStatus(job),
                isPartialFailure: job.partialFailure,
                exitCode: job.summary?.exitCode,
                summaryInfo: addValueToSummaries(job),
            },
            task: {
                taskCount: job.taskCount ?? job.tasks?.length,
                isTaskDetailFolded: job.isFolded === undefined || job.isFolded,
                taskDetailComponent: taskComponent?.[job.action],
                data: job.tasks,
                metadata: job.metadata,
                job: { status: getJobStatus(job), action: job.action },
            },
            action: {
                toggle: () => toggleJobIsExpanded(job.action, job), //이걸 누르면 job이 바뀌면서 filteredJobsData가 바뀌고 rerender가 될텐데 괜찮나?
                retry: () => onRetry(job),
                // cancel: () => cancel(job) //Note: Asset 생성 중 업로드 중단이 가능하면 사용할 것(에셋 빈껍데기가 먼저 생성되고 업로드하는거라 되면 안될지도)
            },
        }));
    }, [jobsData, selectedFilter, filter, onRetry, toggleJobIsExpanded, createAssetJobs.data, getJobStatus]);

    return (
        <React.Fragment>
            {uploadGuide && (
                /* 부연은 상시 노출하지 않는다 — 다국어에서 두 문장이 480px 패널을 넘긴다.
                   describeChild 필수 — 없으면 Tooltip 이 자식의 aria-label 을 덮어써서 스크린리더가
                   배너 본문(경고)을 못 읽고 부연만 읽는다. describeChild 는 aria-describedby 로 덧붙는다.
                   anchorEl 을 ⓘ 로 고정한다 — 기본값인 트리거(480px 배너) 기준이면 필터 줄까지 내려간다. */
                <Tooltip
                    title={uploadGuide.description}
                    describeChild
                    placement={"bottom"}
                    PopperProps={{ anchorEl: () => infoIconRef.current }}
                >
                    <Alert
                        tabIndex={0}
                        severity={uploadGuide.severity}
                        variant={"outlined"}
                        icon={uploadGuide.icon}
                        role={"status"}
                        sx={{
                            borderRadius: 0,
                            borderWidth: "0 0 1px 0",
                            alignItems: "flex-start",
                            px: 1.5,
                            py: 1,
                            //Note: 테마의 outlined Alert 는 severity 를 색으로 구분하지 않아(warning·success 가
                            //배경·테두리 모두 동일) accent 를 직접 입힌다. 색만으로 가르지 않도록 아이콘과 문장이 같은 말을 한다.
                            bgcolor: alpha(uploadGuide.accent, 0.08),
                            borderColor: alpha(uploadGuide.accent, 0.4),
                            ".MuiAlert-icon": { mt: "2px", mr: 1, p: 0 },
                            //Note: 이 저장소는 .MuiAlert-message 를 flex-direction: row 로 덮어 제목과 부연이
                            //가로로 늘어선다(MUI 기본은 block). 여기서 되돌린다. flexGrow 가 없으면 flex 자식이
                            //내용 폭으로 줄어 문구가 일찍 줄바꿈된다.
                            ".MuiAlert-message": { p: 0, display: "block", flexGrow: 1, minWidth: 0 },
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Typography variant={"body3_500"} color={"greySecondary.800"} component={"p"}>
                                {uploadGuide.title}
                            </Typography>
                            {/* 「여기 더 설명이 있다」는 단서 — 없으면 툴팁을 못 찾는다. 배너는 「닫지 마세요」라고
                                하는데 패널 우상단에는 닫기 버튼이 있어서, 「패널은 닫아도 된다」가 툴팁에만 있으면
                                그 모순이 안 풀린다. 장식이므로 스크린리더에서 숨긴다(같은 문장이 aria-describedby 로 붙는다). */}
                            <Box
                                component={"span"}
                                ref={infoIconRef}
                                aria-hidden={"true"}
                                sx={{
                                    display: "inline-flex",
                                    flexShrink: 0,
                                    //Note: 줄 상자가 아니라 글자 중심에 맞춘다. 한글 잉크가 줄 상자 안에서
                                    //아래쪽에 앉아 alignItems:center 만으로는 아이콘이 1px 떠 보인다.
                                    transform: "translateY(1px)",
                                }}
                            >
                                <IconInfoCircle size={12} htmlColor={theme.palette.greySecondary[500]} />
                            </Box>
                        </Box>
                    </Alert>
                </Tooltip>
            )}
            <Grid container pt={3} px={3} pb={2} gap={1} justifyContent={"center"} alignItems={"center"}>
                {filter &&
                    Object.entries(filter)?.map(([key, options]) => (
                        <Grid item xs key={key}>
                            <SelectLimitTags
                                displayEmpty
                                notched
                                label={capitalize(key, true)}
                                labelId={`select-label-asset-jobs-${key}`}
                                multiple={true}
                                value={selectedFilter[key] ?? []}
                                options={options}
                                MenuProps={{
                                    disablePortal: true,
                                }}
                                onChange={(e) => {
                                    onHandleClickFilter(key, e.target.value);
                                }}
                                limitTags={1}
                            />
                        </Grid>
                    ))}
            </Grid>
            <Divider sx={{ mx: 3 }} />
            <Stack flex={1} px={3} py={2.5} gap={4} sx={{ overflowY: "auto", scrollbarGutter: "stable" }}>
                {filteredJobsData.length > 0 ? (
                    <>
                        {filteredJobsData.map((data) => {
                            return <JobItem {...data} key={`asset-job-${data.job.id}`} />;
                        })}
                    </>
                ) : (
                    <JobEmptyList />
                )}
            </Stack>
            {jobsData.length > 0 && (
                <div className="panel-footer" style={{ flex: 0, paddingRight: "10px" }}>
                    {viewmoreEnable ? (
                        <button
                            type={"button"}
                            className={"btn btn-outline-default"}
                            onClick={(e) => {
                                e.preventDefault();
                                onSubmit();
                            }}
                        >
                            <span>{t(`common::label::View More`, `View More`)}</span>
                            <i className="sprite sprite-angle-down" />
                        </button>
                    ) : (
                        ""
                    )}
                </div>
            )}
        </React.Fragment>
    );
};

export default CreateAssetsPanel;
